import {createEntityAdapter, Dictionary, EntityAdapter, EntityState} from '@ngrx/entity';
import {ActionReducer, createReducer, on} from '@ngrx/store';
import {createFeature, createSelector} from '@ngrx/store';
import {VisitPlanActions, VisitPlanAPiActions, VisitPlanChannelActions} from "./visit-plan.actions";
import {VisitPlanModel} from "../../../models/visit-plan.model";
import {Update} from "@ngrx/entity";
import {VisitPlanAccountAPiActions} from "../visit-plan-account-state/visit-plan-account.actions";

export interface VisitPlanState extends EntityState<VisitPlanModel> {
  isLoading: boolean;
}

const adapter: EntityAdapter<VisitPlanModel> = createEntityAdapter<VisitPlanModel>();

const initialState: VisitPlanState = adapter.getInitialState({
  isLoading: false
});


const reducer: ActionReducer<VisitPlanState> = createReducer(
  initialState,

  on(VisitPlanChannelActions.notificationReceived, (state, {notification}) => {
    if (state.entities[notification.id]?.updatedAt < notification.updatedAt) {
      return adapter.setOne(notification, state);
    } else {
      return state;
    }
  }),

  on(VisitPlanChannelActions.partialNotificationReceived, (state, {notification}) => {

    if (state.entities[notification.id]?.updatedAt < notification.updatedAt) {
      const changes: Update<VisitPlanModel> = {
        id: notification.id,
        changes: {...notification}
      }
      return adapter.updateOne(changes, state);
    } else {
      return state;
    }
  }),

  on(VisitPlanAPiActions.getVisitPlan, (state) => ({...state, isLoading: true})),

  on(VisitPlanAPiActions.getVisitPlanSuccess, (state, {visitPlan}) => {
    return adapter.setOne(visitPlan, {...state, isLoading: false})
  }),

  on(VisitPlanAPiActions.getVisitPlanFailure, (state) => ({...state, isLoading: false})),

  on(VisitPlanAPiActions.getVisitPlans, (state) => ({...state, isLoading: true})),

  on(VisitPlanAPiActions.getVisitPlansSuccess, (state, {visitPlans}) => {
    return adapter.setMany(visitPlans, {...state, isLoading: false})
  }),

  on(VisitPlanAPiActions.getVisitPlansFailure, (state) => ({...state, isLoading: false})),


  // Delete
  on(VisitPlanAPiActions.deleteVisitPlan, (state) => ({...state, isLoading: true})),

  on(VisitPlanAPiActions.deleteVisitPlanSuccess, (state, {visitPlanId}) => {
    return adapter.removeOne(visitPlanId, state)
  }),

  on(VisitPlanAPiActions.deleteVisitPlanFailure, (state) => ({...state, isLoading: false})),



  // Restore
  on(VisitPlanAPiActions.restoreVisitPlan, (state) => ({...state, isLoading: true})),

  on(VisitPlanAPiActions.restoreVisitPlanSuccess, (state, {visitPlanId}) => {
    return adapter.removeOne(visitPlanId, state)
  }),

  on(VisitPlanAPiActions.restoreVisitPlanFailure, (state) => ({...state, isLoading: false})),



  on(VisitPlanActions.setVisitPlan, (state, {visitPlan, isAppendFirst}) => {
    return adapter.setOne(visitPlan, {...state, isLoading: false})
  }),
  on(VisitPlanActions.setVisitPlans, (state, {visitPlans, isAppendFirst}) => {
    return adapter.setMany(visitPlans, state)
  }),
  on(VisitPlanActions.removeVisitPlans, (state, {ids}) => adapter.removeMany(ids, state)),

  on(VisitPlanActions.removeAll, (state) => adapter.removeAll(state)),

  on(VisitPlanActions.clearStore, () => ({...initialState}))
);

export const visitPlanFeature = createFeature({
  name: 'VisitPlan',
  reducer,
  extraSelectors: ({
                     selectVisitPlanState,
                     selectEntities,
                     selectIsLoading,
                   }) => ({
    ...adapter.getSelectors(selectVisitPlanState),
    selectVisitPlans: () => createSelector(
      selectEntities,
      (entities: Dictionary<VisitPlanModel>) => Object.values(entities),
    ),
    selectVisitPlansById: (ids: string[]) => createSelector(
      selectEntities,
      (entities: Dictionary<VisitPlanModel>) => {
        const visitPlans = [];
        ids?.forEach(id => {
          if (!!entities[id]) {
            visitPlans.push(entities[id]);
          }
        });
        return visitPlans;
      },
    ),
    selectVisitPlan: (id: string) => createSelector(
      selectEntities,
      (entities: Dictionary<VisitPlanModel>) => entities[id],
    ),
    selectIsLoading: () => createSelector(
      selectIsLoading,
      (isLoading) => isLoading
    )
  })
});

